import { Check, Flag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { apiErrorMessage } from '@/i18n'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import { useDashProjects, useDashTasks, useToggleDashTask } from '../api'
import type { DashProject, ProjectStatus, WidgetBodyProps } from '../types'
import { TONE_STYLES } from './tones'
import { WidgetItems, WidgetState } from './WidgetItems'

const todayIso = () => new Date().toISOString().slice(0, 10)

export function MyTasksWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useDashTasks()
  const toggle = useToggleDashTask()
  const today = todayIso()
  const tasks = [...(q.data ?? [])].sort((a, b) => Number(a.done) - Number(b.done) || a.due_on.localeCompare(b.due_on))

  const onToggle = (id: string) =>
    toggle.mutate(id, { onError: (err) => toast.error(apiErrorMessage(err)) })

  const items = tasks.map((task) => {
    const isToday = task.due_on === today && !task.done
    return {
      id: task.id,
      title: task.title,
      muted: task.done,
      leading: (
        <button
          type="button"
          role="checkbox"
          aria-checked={task.done}
          aria-label={t('dashboard.widgets.myTasks.toggle', { title: task.title })}
          onClick={() => onToggle(task.id)}
          className={cn(
            'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
            task.done ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card hover:border-primary',
          )}
        >
          {task.done ? <Check className="size-3" strokeWidth={3} /> : null}
        </button>
      ),
      meta: isToday ? <span className="font-medium text-destructive">{t('common.today')}</span> : fmtDate(task.due_on),
      trailing: task.high_priority && !task.done ? <Flag className="size-3.5 fill-destructive text-destructive" aria-label={t('dashboard.widgets.myTasks.highPriority')} /> : undefined,
    }
  })

  return (
    <WidgetState query={q} empty={items.length === 0} emptyText={t('dashboard.widgets.myTasks.empty')}>
      <WidgetItems items={items} view={config.view} density={config.density} />
    </WidgetState>
  )
}

const STATUS_TONE: Record<ProjectStatus, BadgeTone> = { active: 'info', planning: 'neutral', waiting: 'warning', done: 'success' }

function Avatars({ members, extra }: { members: string[]; extra: number }) {
  return (
    <div className="flex -space-x-1.5">
      {members.map((m) => (
        <span key={m} className="flex size-6 items-center justify-center rounded-full border-2 border-card bg-accent text-[10px] font-semibold text-accent-foreground">
          {m}
        </span>
      ))}
      {extra > 0 ? (
        <span className="flex size-6 items-center justify-center rounded-full border-2 border-card bg-muted text-[10px] font-medium text-muted-foreground">+{extra}</span>
      ) : null}
    </div>
  )
}

function Progress({ value, barClass }: { value: number; barClass: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="h-1.5 min-w-12 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', barClass)} style={{ width: `${value}%` }} />
      </div>
      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">%{value}</span>
    </div>
  )
}

function ProjectRow({ project, compact, barClass }: { project: DashProject; compact: boolean; barClass: string }) {
  const { t } = useTranslation()
  return (
    <li className={cn('grid grid-cols-[minmax(0,1fr)_minmax(90px,140px)] items-center gap-3 @lg:grid-cols-[minmax(0,1fr)_auto_minmax(110px,180px)_auto_auto]', compact ? 'py-1.5' : 'py-2.5')}>
      <span className="truncate text-[13px] font-medium">{project.name}</span>
      <Badge tone={STATUS_TONE[project.status]} className="hidden @lg:inline-flex">
        {t(`dashboard.widgets.projects.status.${project.status}`)}
      </Badge>
      <Progress value={project.progress} barClass={barClass} />
      <span className="hidden text-xs text-muted-foreground @lg:block">{t('dashboard.widgets.projects.due', { date: fmtDate(project.due_on) })}</span>
      <span className="hidden @lg:block">
        <Avatars members={project.members} extra={project.extra} />
      </span>
    </li>
  )
}

export function ProjectsWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useDashProjects()
  const projects = q.data ?? []
  const compact = config.density === 'compact'
  const barClass = TONE_STYLES[config.tone].bar

  return (
    <WidgetState query={q} empty={projects.length === 0} emptyText={t('dashboard.widgets.projects.empty')}>
      {config.view === 'card' ? (
        <ul className={cn('grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))]', compact ? 'gap-1.5' : 'gap-2')}>
          {projects.map((p) => (
            <li key={p.id} className={cn('space-y-2 rounded-xl border bg-muted/30 dark:bg-muted/15', compact ? 'p-2' : 'p-3')}>
              <div className="flex items-start justify-between gap-2">
                <span className="truncate text-[13px] font-medium">{p.name}</span>
                <Badge tone={STATUS_TONE[p.status]}>{t(`dashboard.widgets.projects.status.${p.status}`)}</Badge>
              </div>
              <Progress value={p.progress} barClass={barClass} />
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                {fmtDate(p.due_on)}
                <Avatars members={p.members} extra={p.extra} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="@container divide-y divide-border/60">
          {projects.map((p) => (
            <ProjectRow key={p.id} project={p} compact={compact} barClass={barClass} />
          ))}
        </ul>
      )}
    </WidgetState>
  )
}
